import { Loader } from '@ksyos/design-system';
import { isBoolean } from 'lodash';
import { useEffect, useState } from 'react';
import { SelectSleepRecorder } from '@frontend/components/elements/SelectSleepRecorder';
import type { ValidationErrors } from '../../../../../utils/validation';
import type { IServiceOrganizationSettings } from '../../../../interfaces/serviceOrganizationSettings';
import type { ICustomControlDataOsasv } from './OsasvV1RequestAddEdit';
import { fetchJson } from '../../../../../store/fetch';
import { FormContextProvider } from '../../../../elements/formContext';
import { TextAreaField } from '../../../../fields/TextArea';

interface IReferralAddEditKsyosMedicalCenterProps {
    customControlData: ICustomControlDataOsasv;
    errors: ValidationErrors;
    handleChangeCustomControl: (customControlData: Partial<ICustomControlDataOsasv>) => Promise<void>;
    organizationId: string;
    serviceId: string;
    validateOneCustomControl: (name: string) => Promise<void>;
}

export function ReferralAddEditKsyosMedicalCenter(props: IReferralAddEditKsyosMedicalCenterProps) {
    const [canReferPatientWithoutSmartphone, setCanReferPatientWithoutSmartphone] = useState<boolean | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        getServiceOrganizationSettings();
    }, [props.organizationId]);

    async function getServiceOrganizationSettings() {
        setIsLoading(true);

        const url = `/api/serviceOrganizationSettings?serviceId=${props.serviceId}&organizationId=${props.organizationId}&isActive=true`;
        const result = await fetchJson(url, 'GET');

        if (result.json.items?.length === 1) {
            const settings: IServiceOrganizationSettings = result.json.items[0];

            setCanReferPatientWithoutSmartphone(settings.canReferPatientWithoutSmartphone);
        }

        setIsLoading(false);
    }

    return (
        <>
            {isLoading ? (
                <Loader
                    className="mt-3 mx-auto"
                    size="large"
                />
            ) : (
                <FormContextProvider
                    value={{
                        placeholderGroupName: 'customControlData',
                        item: props.customControlData,
                        errors: props.errors,
                        customControlData: true,
                        onChange: async (key, value) => await props.handleChangeCustomControl({ [key]: value }),
                        onDoValidate: async (key) => await props.validateOneCustomControl(key),
                    }}
                >
                    <TextAreaField
                        automation="reasonReferral"
                        fieldKey="verwijsreden"
                        isRequired
                        label="Verwijsreden"
                        placeholder="Verwijsreden"
                    />

                    <TextAreaField
                        automation="mainComplaint"
                        fieldKey="hoofdklacht"
                        isRequired
                        label="Hoofdklacht"
                        placeholder="Omschrijf de hoofdklacht..."
                    />

                    <TextAreaField
                        automation="voorgeschiedenis"
                        fieldKey="voorgeschiedenis"
                        isRequired
                        label="Voorgeschiedenis"
                        placeholder="Omschrijf de voorgeschiedenis..."
                    />
                    {isBoolean(canReferPatientWithoutSmartphone) && (
                        <SelectSleepRecorder
                            canReferPatientWithoutSmartphone={canReferPatientWithoutSmartphone}
                            errors={props.errors}
                            hasErrors={
                                props.errors.has('customControlData.typeSlaaprecorder') ||
                                props.errors.has('customControlData.slaaprecorderVerzenden')
                            }
                            onChange={async ({ distributionMethod, patientHasSmartphone, sleepRecorderType }) => {
                                await props.handleChangeCustomControl({
                                    typeSlaaprecorder: sleepRecorderType,
                                    slaaprecorderVerzenden: distributionMethod,
                                    patientHasSmartphone,
                                });

                                if (props.errors.size) {
                                    await Promise.all([
                                        props.validateOneCustomControl('typeSlaaprecorder'),
                                        props.validateOneCustomControl('slaaprecorderVerzenden'),
                                    ]);
                                }
                            }}
                            values={{
                                distributionMethod: props.customControlData.slaaprecorderVerzenden,
                                sleepRecorderType: props.customControlData.typeSlaaprecorder,
                                patientHasSmartphone: props.customControlData.patientHasSmartphone,
                            }}
                        />
                    )}
                </FormContextProvider>
            )}
        </>
    );
}
